/**
 * Een menukaart die is ingelezen maar nog niet is goedgekeurd.
 *
 * Wat er uit een foto van een kaart komt is nooit meteen goed. Een prijs die
 * half wegvalt, een BTW-tarief dat nergens op de kaart staat, een kop die als
 * product wordt gelezen. Daarom is de volgorde: inlezen, de kraam laat het
 * nakijken, de organisator keurt goed. Pas daarna staat het in de kassa.
 *
 * Regel: wat het model niet zeker weet wordt gemarkeerd, niet gegokt. Een
 * gegokte prijs die niemand nakijkt komt terecht op een bon.
 */

import { BTW_HOOG, BTW_LAAG, isBtwTarief, type BtwTarief } from './btw.js';
import { type Cent } from './geld.js';

export interface Menuconceptregel {
  naam: string;
  prijsCent: Cent | null;
  btwTarief: BtwTarief | null;
  categorie: string;
  /** Het aantal bekers dat bij dit product hoort, meestal 0 of 1. */
  bekers: number;
  /** Waar het model niet zeker van was. Leeg betekent: dit stond er gewoon. */
  onzeker: string[];
}

export interface Menuconcept {
  regels: Menuconceptregel[];
  /** Wat er op de kaart stond maar niet als product te lezen was. */
  overgeslagen: string[];
}

/**
 * Wat de kraam moet nakijken voordat hij het instuurt. Zolang hier iets in
 * staat kan het menu niet ter goedkeuring.
 */
export function watMoetNagekeken(concept: Menuconcept): Menuconceptregel[] {
  return concept.regels.filter(
    (regel) =>
      regel.onzeker.length > 0 ||
      regel.prijsCent === null ||
      regel.btwTarief === null ||
      regel.naam.trim() === '',
  );
}

export function isKlaarVoorGoedkeuring(concept: Menuconcept): boolean {
  return concept.regels.length > 0 && watMoetNagekeken(concept).length === 0;
}

/**
 * Het BTW-tarief dat we voorstellen als het model het niet zeker weet.
 *
 * Eten en fris is 9%, alcohol is 21%. Dit is een voorstel dat gemarkeerd blijft
 * staan tot de kraam het bevestigt: de kraam is verantwoordelijk voor het
 * juiste tarief, en dat staat ook zo in de kraamovereenkomst.
 */
const ALCOHOLWOORDEN = [
  'bier',
  'pils',
  'wijn',
  'wodka',
  'rum',
  'gin',
  'whisky',
  'likeur',
  'cocktail',
  'prosecco',
  'champagne',
  'aperol',
  'spritz',
  'jenever',
  'shot',
  'radler',
  'ipa',
  'speciaalbier',
];

export function stelBtwTariefVoor(productnaam: string): {
  tarief: BtwTarief;
  zeker: boolean;
} {
  const naam = productnaam.toLowerCase();
  if (ALCOHOLWOORDEN.some((woord) => naam.includes(woord))) {
    return { tarief: BTW_HOOG, zeker: false };
  }
  return { tarief: BTW_LAAG, zeker: false };
}

export interface RuweRegel {
  naam?: unknown;
  prijsCent?: unknown;
  btwTarief?: unknown;
  categorie?: unknown;
  bekers?: unknown;
  /**
   * Waar het model zelf over twijfelde. Een prijs die het wel leest maar niet
   * vertrouwt komt hier binnen: de waarde blijft staan als voorstel, maar de
   * regel blijft rood tot de kraam hem bevestigt.
   */
  onzeker?: unknown;
}

// Alleen de velden die bevestigRegel ook opnieuw beoordeelt. Zet er een veld
// bij dat daar niet in staat, dan blijft die twijfel voor altijd hangen of
// verdwijnt hij bij de eerste de beste wijziging elders in de regel.
const ONZEKERE_VELDEN = ['naam', 'prijs', 'btw'];

/**
 * Maakt van wat het model teruggeeft een regel waar de kraam mee verder kan.
 *
 * Alles wat ontbreekt of niet klopt wordt gemarkeerd in plaats van ingevuld.
 * Een lege prijs is beter dan een verzonnen prijs.
 */
export function leesRegel(ruw: RuweRegel): Menuconceptregel {
  const onzeker: string[] = [];

  const naam = typeof ruw.naam === 'string' ? ruw.naam.trim() : '';
  if (naam === '') onzeker.push('naam');

  let prijsCent: Cent | null = null;
  if (
    typeof ruw.prijsCent === 'number' &&
    Number.isSafeInteger(ruw.prijsCent) &&
    ruw.prijsCent >= 0
  ) {
    prijsCent = ruw.prijsCent;
  } else {
    onzeker.push('prijs');
  }

  let btwTarief: BtwTarief | null = null;
  if (isBtwTarief(ruw.btwTarief)) {
    btwTarief = ruw.btwTarief;
  } else {
    // Een voorstel doen mag, maar het blijft gemarkeerd tot de kraam het ziet.
    btwTarief = stelBtwTariefVoor(naam).tarief;
    onzeker.push('btw');
  }

  const categorie =
    typeof ruw.categorie === 'string' && ruw.categorie.trim() !== ''
      ? ruw.categorie.trim()
      : 'Overig';

  const bekers =
    typeof ruw.bekers === 'number' && Number.isSafeInteger(ruw.bekers) && ruw.bekers >= 0
      ? ruw.bekers
      : 0;

  // Wat het model zelf niet vertrouwde telt mee, ook als het veld gevuld is.
  if (Array.isArray(ruw.onzeker)) {
    for (const veld of ruw.onzeker) {
      if (typeof veld === 'string' && ONZEKERE_VELDEN.includes(veld) && !onzeker.includes(veld)) {
        onzeker.push(veld);
      }
    }
  }

  return { naam, prijsCent, btwTarief, categorie, bekers, onzeker };
}

export function leesConcept(
  ruweRegels: readonly RuweRegel[],
  overgeslagen: readonly string[] = [],
): Menuconcept {
  return {
    regels: ruweRegels.map(leesRegel),
    overgeslagen: [...overgeslagen],
  };
}

/** Een regel die de kraam heeft nagekeken is niet langer onzeker. */
export function bevestigRegel(
  regel: Menuconceptregel,
  wijziging: Partial<
    Pick<Menuconceptregel, 'naam' | 'prijsCent' | 'btwTarief' | 'categorie' | 'bekers'>
  >,
): Menuconceptregel {
  const bijgewerkt = { ...regel, ...wijziging };
  const nogOnzeker: string[] = [];

  if (bijgewerkt.naam.trim() === '') nogOnzeker.push('naam');
  if (bijgewerkt.prijsCent === null) nogOnzeker.push('prijs');
  if (bijgewerkt.btwTarief === null) nogOnzeker.push('btw');

  return { ...bijgewerkt, onzeker: nogOnzeker };
}
